import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Box,
  Button,
  Card,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded'
import {
  adminService,
  type Analytics,
  type AnalyticsRange,
  type AnalyticsStageAttendee,
} from '../../services/adminService'
import { apiError } from '../errors'
import { useToast } from '../useToast'

/** Map a funnel stage label to the drill-down stage key the API expects. */
const STAGE_KEY: Record<string, string> = {
  Invited: 'invited',
  Activated: 'activated',
  Registered: 'registered',
  'Checked in': 'checked_in',
}

function triggerDownload(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  a.click()
  URL.revokeObjectURL(url)
}

/** Advanced analytics: funnel, engagement, timeline, selection breakdown,
 *  with a date-range filter, per-stage drill-downs, and CSV exports. */
export default function AnalyticsSection({ eventId }: { eventId: string }) {
  const { notify } = useToast()
  const [data, setData] = useState<Analytics | null>(null)
  const [loading, setLoading] = useState(false)

  // Date-range filter (empty = all-time). Bound to <input type="date">.
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')
  const range: AnalyticsRange = useMemo(() => ({ from: from || undefined, to: to || undefined }), [from, to])

  // Export menu anchor.
  const [exportAnchor, setExportAnchor] = useState<null | HTMLElement>(null)

  // Drill-down dialog state.
  const [drill, setDrill] = useState<{ stage: string; label: string } | null>(null)
  const [drillRows, setDrillRows] = useState<AnalyticsStageAttendee[] | null>(null)
  const [drillSearch, setDrillSearch] = useState('')

  const load = useCallback(() => {
    setLoading(true)
    adminService
      .analytics(eventId, range)
      .then(setData)
      .catch((e) => notify(apiError(e), 'error'))
      .finally(() => setLoading(false))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId, from, to])

  useEffect(() => {
    load()
  }, [load])

  const exportCsv = async (kind: 'funnel' | 'engagement') => {
    setExportAnchor(null)
    try {
      const blob =
        kind === 'funnel'
          ? await adminService.exportAnalytics(eventId, range)
          : await adminService.exportEngagement(eventId, range)
      triggerDownload(blob, `analytics-${kind}.csv`)
    } catch (e) {
      notify(apiError(e), 'error')
    }
  }

  const openDrill = async (label: string) => {
    const stage = STAGE_KEY[label]
    if (!stage) return
    setDrill({ stage, label })
    setDrillRows(null)
    setDrillSearch('')
    try {
      const rows = await adminService.analyticsStage(eventId, stage, range)
      setDrillRows(rows)
    } catch (e) {
      notify(apiError(e), 'error')
      setDrill(null)
    }
  }

  const filteredDrill = useMemo(() => {
    if (!drillRows) return []
    const q = drillSearch.trim().toLowerCase()
    if (!q) return drillRows
    return drillRows.filter(
      (r) =>
        r.email.toLowerCase().includes(q) ||
        (r.fullName || '').toLowerCase().includes(q) ||
        (r.company || '').toLowerCase().includes(q),
    )
  }, [drillRows, drillSearch])

  if (!data) return null

  const top = data.funnel[0]?.count || 0
  const eng = data.engagement
  const timelineMax = Math.max(1, ...data.registrationTimeline.map((t) => t.count))
  const rangeActive = Boolean(from || to)

  return (
    <Box sx={{ mt: 4 }}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'stretch', sm: 'center' }}
        spacing={2}
        sx={{ mb: 2 }}
      >
        <Typography variant="h5" fontWeight={800}>
          Analytics
        </Typography>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          <TextField
            type="date"
            size="small"
            label="From"
            InputLabelProps={{ shrink: true }}
            value={from}
            onChange={(e) => setFrom(e.target.value)}
            inputProps={{ max: to || undefined }}
          />
          <TextField
            type="date"
            size="small"
            label="To"
            InputLabelProps={{ shrink: true }}
            value={to}
            onChange={(e) => setTo(e.target.value)}
            inputProps={{ min: from || undefined }}
          />
          {rangeActive && (
            <Button
              size="small"
              onClick={() => {
                setFrom('')
                setTo('')
              }}
            >
              Clear
            </Button>
          )}
          {loading && <CircularProgress size={18} />}
          <Button
            variant="outlined"
            startIcon={<DownloadRoundedIcon />}
            onClick={(e) => setExportAnchor(e.currentTarget)}
          >
            Export
          </Button>
          <Menu anchorEl={exportAnchor} open={Boolean(exportAnchor)} onClose={() => setExportAnchor(null)}>
            <MenuItem onClick={() => exportCsv('funnel')}>Funnel (per attendee) CSV</MenuItem>
            <MenuItem onClick={() => exportCsv('engagement')}>Engagement summary CSV</MenuItem>
          </Menu>
        </Stack>
      </Stack>

      {rangeActive && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
          Showing activity{from ? ` from ${from}` : ''}{to ? ` to ${to}` : ''}. Registrations and
          check-ins are counted within this window.
        </Typography>
      )}

      {/* Funnel */}
      <Card variant="outlined" sx={{ p: 2.5, borderRadius: 3, mb: 3 }}>
        <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2 }}>
          Attendee funnel
        </Typography>
        <Stack spacing={1.5}>
          {data.funnel.map((f) => {
            const pct = top ? Math.round((f.count / top) * 100) : 0
            const drillable = Boolean(STAGE_KEY[f.stage])
            return (
              <Box
                key={f.stage}
                onClick={drillable ? () => openDrill(f.stage) : undefined}
                sx={{
                  cursor: drillable ? 'pointer' : 'default',
                  borderRadius: 1.5,
                  p: 0.75,
                  mx: -0.75,
                  transition: 'background-color .15s',
                  '&:hover': drillable ? { bgcolor: 'action.hover' } : undefined,
                }}
              >
                <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
                  <Stack direction="row" alignItems="center" spacing={0.5}>
                    <Typography variant="body2" fontWeight={600}>
                      {f.stage}
                    </Typography>
                    {drillable && (
                      <ChevronRightRoundedIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
                    )}
                  </Stack>
                  <Typography variant="body2" color="text.secondary">
                    {f.count} ({Math.round(f.pctOfTop * 100)}%)
                  </Typography>
                </Stack>
                <LinearProgress variant="determinate" value={pct} sx={{ height: 10, borderRadius: 5 }} />
              </Box>
            )
          })}
        </Stack>
        <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 1.5 }}>
          Tip: click a stage to see the attendees behind it.
        </Typography>
      </Card>

      {/* Engagement KPIs */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[
          { label: 'Poll votes', value: eng.pollVotes },
          { label: 'Questions', value: eng.questions },
          { label: 'Connections', value: eng.connections },
          { label: 'Meetings', value: eng.meetings },
          { label: 'Sponsor leads', value: eng.sponsorLeads },
          {
            label: 'Campaign opens',
            value: `${eng.campaignOpened}/${eng.campaignSent}`,
            sub: `${Math.round(eng.campaignOpenRate * 100)}% open rate`,
          },
        ].map((k) => (
          <Grid item xs={6} md={2} key={k.label}>
            <Card variant="outlined" sx={{ p: 2, borderRadius: 3, textAlign: 'center' }}>
              <Typography variant="h5" fontWeight={800}>
                {k.value}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {k.label}
              </Typography>
              {k.sub && (
                <Typography variant="caption" color="text.secondary" display="block">
                  {k.sub}
                </Typography>
              )}
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Registration timeline */}
      {data.registrationTimeline.length > 0 ? (
        <Card variant="outlined" sx={{ p: 2.5, borderRadius: 3, mb: 3 }}>
          <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2 }}>
            Registrations over time
          </Typography>
          <Stack direction="row" spacing={1} alignItems="flex-end" sx={{ height: 120 }}>
            {data.registrationTimeline.map((t) => (
              <Box key={t.date} sx={{ flex: 1, textAlign: 'center' }}>
                <Tooltip title={`${t.count} on ${t.date}`} arrow>
                  <Box
                    sx={{
                      height: `${(t.count / timelineMax) * 100}%`,
                      bgcolor: 'primary.main',
                      borderRadius: 1,
                      minHeight: 4,
                    }}
                  />
                </Tooltip>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem' }}>
                  {t.date.slice(5)}
                </Typography>
              </Box>
            ))}
          </Stack>
        </Card>
      ) : (
        rangeActive && (
          <Card variant="outlined" sx={{ p: 2.5, borderRadius: 3, mb: 3 }}>
            <Typography variant="body2" color="text.secondary">
              No registrations in the selected date range.
            </Typography>
          </Card>
        )
      )}

      {/* Selection breakdown */}
      {data.selectionBreakdown.map((g) => (
        <Card key={g.group} variant="outlined" sx={{ p: 2.5, borderRadius: 3, mb: 2 }}>
          <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1.5 }}>
            {g.group}
          </Typography>
          <Stack spacing={1}>
            {g.options.map((o) => {
              const gTotal = g.options.reduce((s, x) => s + x.count, 0) || 1
              const pct = Math.round((o.count / gTotal) * 100)
              return (
                <Box key={o.title}>
                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="body2">{o.title}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {o.count} ({pct}%)
                    </Typography>
                  </Stack>
                  <LinearProgress variant="determinate" value={pct} sx={{ height: 6, borderRadius: 3 }} />
                </Box>
              )
            })}
          </Stack>
        </Card>
      ))}

      <Divider sx={{ my: 2 }} />
      <Typography variant="caption" color="text.secondary">
        Generated {new Date(data.generatedAt).toLocaleString()}
      </Typography>

      {/* Drill-down dialog */}
      <Dialog open={Boolean(drill)} onClose={() => setDrill(null)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ pr: 6 }}>
          {drill?.label} attendees
          {drillRows && (
            <Typography component="span" color="text.secondary" sx={{ ml: 1 }}>
              ({filteredDrill.length})
            </Typography>
          )}
          <IconButton
            onClick={() => setDrill(null)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
            size="small"
          >
            <CloseRoundedIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {!drillRows ? (
            <Stack alignItems="center" sx={{ py: 4 }}>
              <CircularProgress size={24} />
            </Stack>
          ) : (
            <>
              <TextField
                fullWidth
                size="small"
                placeholder="Search name, email, or company…"
                value={drillSearch}
                onChange={(e) => setDrillSearch(e.target.value)}
                sx={{ mb: 1.5 }}
              />
              {filteredDrill.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                  No matching attendees.
                </Typography>
              ) : (
                <List dense disablePadding>
                  {filteredDrill.map((a) => (
                    <ListItem key={a.id} disableGutters divider>
                      <ListItemText
                        primary={a.fullName || a.email}
                        secondary={
                          [a.email, a.company, a.track].filter(Boolean).join(' · ') || undefined
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  )
}
